import { AddCircleOutline, Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from "@mui/material";
import React, { memo, useState } from "react";
import { useSelector } from "react-redux";
import Checkbox from "../../../../../../../Components/CheckBox/Checkbox";
import { nodeInputStyles } from "../../GeneralNodeStyles";
import EditPromptsDialog from "../EditPromptsDialog";
import QueryChainingDialog from "../QueryChainingDialog";
import {
  primaryButtonStyles,
  tertiaryButtonStyles,
} from "../../../../../../../Constants/buttonStyles";
import theme, {
  createLLMTypography,
} from "../../../../../../../Constants/theme";
const QueryTransforms = memo(
  ({
    data,
    queryTransform,
    setQueryTransform,
    prompts,
    setPrompts,
    chainingPromptsQT,
    setChainingPromptsQT,
    sequenceHistory,
    setSequenceHistory,
    selectedChainItems,
    setSelectedChainItems,
  }) => {
    const { botData, setBotData, nodeId } = data;

    const organization = useSelector((state) => state.organization);
    // open edit prompts dialog
    const [openDialog, setOpenDialog] = useState(false);
    // open query chaning dialog
    const [dialogQCOpen, setDialogQCOpen] = useState(false);

    const updatePrompt = (key, newPrompt) => {
      setPrompts((prev) => ({ ...prev, [key]: newPrompt }));
    };

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const handleQueryChange = (event, type) => {
      const query_t = { ...queryTransform };
      query_t[type] = event.target.checked;

      // Check if any checkbox (in query_t or selectedChainItems) is selected
      const isAnyCheckboxSelected =
        Object.values(query_t).some((value) => value) ||
        selectedChainItems.length > 0;

      // If none are selected, default to "basic"
      if (!isAnyCheckboxSelected) {
        query_t["basic"] = true;
      }

      setQueryTransform({ ...query_t });
      localStorage.setItem(`queryTransform_${nodeId}`, JSON.stringify(query_t));

      setBotData((prevData) => ({
        ...prevData,
        workflow: {
          ...prevData.workflow,
          [`${nodeId}`]: {
            ...prevData.workflow[`${nodeId}`],
            query_preprocess_method: query_t,
          },
        },
      }));
    };

    // Toggle selection of a list item when checkbox is clicked
    const handleCheckboxChange = (key) => {
      setSelectedChainItems((prevSelectedItems) => {
        if (prevSelectedItems.includes(key)) {
          // Deselect if already selected

          setBotData((prevData) => ({
            ...prevData,
            workflow: {
              ...prevData.workflow,
              [`${nodeId}`]: {
                ...prevData.workflow[`${nodeId}`],

                query_preprocess_method: {
                  ...prevData.workflow[nodeId].query_preprocess_method,
                  [sequenceHistory[key]]: false,
                },
              },
            },
          }));
          localStorage.setItem(
            `queryTransform_${nodeId}`,
            JSON.stringify({
              ...botData.workflow[nodeId].query_preprocess_method,
              [sequenceHistory[key]]: false,
            })
          );
          return prevSelectedItems.filter((item) => item !== key);
        } else {
          setBotData((prevData) => ({
            ...prevData,
            workflow: {
              ...prevData.workflow,
              [`${nodeId}`]: {
                ...prevData.workflow[`${nodeId}`],

                query_preprocess_method: {
                  ...prevData.workflow[nodeId].query_preprocess_method,
                  [sequenceHistory[key]]: true,
                },
              },
            },
          }));
          localStorage.setItem(
            `queryTransform_${nodeId}`,
            JSON.stringify({
              ...botData.workflow[nodeId].query_preprocess_method,
              [sequenceHistory[key]]: true,
            })
          );
          return [...prevSelectedItems, key];
        }
      });
    };

    function mapSequence(sequence) {
      const mapping = {
        multi_query: "MP",
        hyde: "H",
        query_expansion: "E",
        complex_to_simple: "D",
        iterative_deconstruction: "ID",
      };

      if (sequence && sequence.includes("→")) {
        return sequence
          .split("→")
          .map((item) => mapping[item.toLowerCase()] || item)
          .join("→");
      } else {
        return sequence;
      }
    }

    function mapSequenceTitle(sequence) {
      const mapping = {
        multi_query: "Multi-Phrasing",
        hyde: "HyDE",
        query_expansion: "Expansion",
        complex_to_simple: "Deconstruction",
        iterative_deconstruction: "Iterative Deconstruction",
      };

      if (sequence && sequence.includes("→")) {
        return sequence
          .split("→")
          .map((item) => mapping[item.toLowerCase()] || item)
          .join("→");
      } else {
        return sequence;
      }
    }

    const handeOpenClickChaining = () => {
      if (sequenceHistory?.length < 3) {
        setChainingPromptsQT("");
        setDialogQCOpen(true);
      }
    };

    const handleRemoveSequence = (index) => {
      // Step 1: Update sequenceHistory
      setSequenceHistory((prev) => prev.filter((_, i) => i !== index));

      // Step 2: Remove the item from botData
      setBotData((prevData) => {
        const updatedQueryPreprocessMethod = {
          ...prevData.workflow[nodeId].query_preprocess_method,
        };
        delete updatedQueryPreprocessMethod[sequenceHistory[index]];

        return {
          ...prevData,
          workflow: {
            ...prevData.workflow,
            [nodeId]: {
              ...prevData.workflow[nodeId],
              query_preprocess_method: updatedQueryPreprocessMethod,
            },
          },
        };
      });

      // Step 3: Update localStorage
      const updatedLocalStorageData = {
        ...botData.workflow[nodeId].query_preprocess_method,
      };
      delete updatedLocalStorageData[sequenceHistory[index]];
      localStorage.setItem(
        `queryTransform_${nodeId}`,
        JSON.stringify(updatedLocalStorageData)
      );
    };

    return (
      <Box
        sx={{
          ...nodeInputStyles,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Checkbox
          label="None"
          onChange={(event) => handleQueryChange(event, "basic")}
          checked={queryTransform?.["basic"]}
        />
        <Checkbox
          label="Multi-phrasing"
          onChange={(event) => handleQueryChange(event, "multi_query")}
          checked={queryTransform?.["multi_query"]}
        />
        <Checkbox
          label="HyDE"
          onChange={(event) => handleQueryChange(event, "hyde")}
          checked={queryTransform?.["hyde"]}
        />
        <Checkbox
          disabled={organization?.organization?.subscription === "starter"}
          tooltipText={
            organization?.organization?.subscription === "starter" &&
            "You need Pro subscription to access this feature."
          }
          tooltipPlacement="left"
          label="Deconstruction"
          onChange={(event) => handleQueryChange(event, "complex_to_simple")}
          checked={queryTransform?.["complex_to_simple"]}
        />
        <Checkbox
          disabled={organization?.organization?.subscription === "starter"}
          tooltipText={
            organization?.organization?.subscription === "starter" &&
            "You need Pro subscription to access this feature."
          }
          tooltipPlacement="left"
          label="Expansion"
          onChange={(event) => handleQueryChange(event, "query_expansion")}
          checked={queryTransform?.["query_expansion"]}
        />
        <Checkbox
          disabled={organization?.organization?.subscription === "starter"}
          tooltipText={
            organization?.organization?.subscription === "starter" &&
            "You need Pro subscription to access this feature."
          }
          tooltipPlacement="left"
          label="Iterative Deconstruction"
          onChange={(event) =>
            handleQueryChange(event, "iterative_deconstruction")
          }
          checked={queryTransform?.["iterative_deconstruction"]}
        />
        <List dense disablePadding>
          {sequenceHistory.map((sequence, index) => (
            <ListItem
              key={index}
              sx={{
                px: 0.5,
                py: 0,
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "inline-block", width: "auto", mr: 0.5 }}>
                <Checkbox
                  checked={selectedChainItems.includes(index)}
                  onChange={() => handleCheckboxChange(index)}
                  tooltipText={mapSequenceTitle(sequence)}
                  customStyles={{
                    margin: 0,
                    padding: 0,
                    whiteSpace: "nowrap", // keep label on one line
                    fontSize: createLLMTypography.fontSizes.sectionTitle,
                    color: theme.inputs.textColor, // your theme text color
                  }}
                />
              </Box>

              <ListItemText
                primary={
                  <Tooltip title={mapSequenceTitle(sequence)}>
                    {mapSequence(sequence)}
                  </Tooltip>
                }
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  "& .MuiListItemText-primary": {
                    fontSize: createLLMTypography.fontSizes.sectionTitle,
                    color: theme.textColors.dark.primary,
                  },
                }}
              />

              <ListItemSecondaryAction>
                <Clear
                  sx={{
                    color: theme.textColors.dark.primary,
                    fontSize: createLLMTypography.fontSizes.sectionTitle,
                  }}
                  onClick={() => handleRemoveSequence(index)}
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Tooltip
          title={
            organization?.organization?.subscription === "starter"
              ? "You need a pro subscription to access this feature"
              : sequenceHistory?.length >= 3
              ? "You can only add up to 3 custom chains"
              : "Create a custom query transformation sequence."
          }
        >
          <Button
            sx={tertiaryButtonStyles}
            disabled={
              sequenceHistory?.length >= 3 ||
              organization?.organization?.subscription === "starter"
            }
            onClick={handeOpenClickChaining}
          >
            <AddCircleOutline style={{ fontSize: "1rem" }} />
            Add Query Chain
          </Button>
        </Tooltip>
        <Tooltip title={"Edit Query transformation prompts"}>
          <Button onClick={handleOpenDialog} sx={primaryButtonStyles}>
            Edit Prompts
          </Button>
        </Tooltip>

        <EditPromptsDialog
          open={openDialog}
          onClose={handleCloseDialog}
          prompts={prompts}
          updatePrompt={updatePrompt}
          setBotData={setBotData}
          nodeId={nodeId}
          botData={botData}
          organization={organization}
        />
        <QueryChainingDialog
          dialogOpen={dialogQCOpen}
          setDialogOpen={setDialogQCOpen}
          chainingPromptsQT={chainingPromptsQT}
          setChainingPromptsQT={setChainingPromptsQT}
          sequenceHistory={sequenceHistory}
          setSequenceHistory={setSequenceHistory}
          setSelectedChainItems={setSelectedChainItems}
          selectedChainItems={selectedChainItems}
          botData={botData}
          setBotData={setBotData}
          nodeId={nodeId}
        />
      </Box>
    );
  }
);

export default QueryTransforms;
